/*
 * GasApp store: the OBD reader in 3D (three.js, built from shapes; no model file).
 *
 * Mounts on every [data-ga-reader] element:
 *   data-mode="spin"   turns on its own; drag or flick to spin it.
 *   data-mode="scroll" turns a full 360° as its [data-ga-story] section scrolls by.
 * The element's .ga-poster stays visible until the first frame renders, and
 * also when WebGL isn't available.
 */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';

const BLUE = new THREE.Color('#3B5BFF');
const CYAN = new THREE.Color('#22D3EE');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- shapes ---------- */

function roundedRect(w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/** The OBD-II plug's outline: a trapezoid, wide edge on top, rounded corners. */
function trapezoid(top, bottom, h, r, path = new THREE.Shape()) {
  const pts = [
    [-top / 2, h / 2],
    [top / 2, h / 2],
    [bottom / 2, -h / 2],
    [-bottom / 2, -h / 2],
  ];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i + n - 1) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const toward = (q) => {
      const dx = q[0] - p1[0];
      const dy = q[1] - p1[1];
      const len = Math.hypot(dx, dy);
      return [p1[0] + (dx / len) * r, p1[1] + (dy / len) * r];
    };
    const a = toward(p0);
    const b = toward(p2);
    if (i === 0) path.moveTo(a[0], a[1]);
    else path.lineTo(a[0], a[1]);
    path.quadraticCurveTo(p1[0], p1[1], b[0], b[1]);
  }
  path.closePath();
  return path;
}

function radialTexture(stops) {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  stops.forEach(([at, color]) => grad.addColorStop(at, color));
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Paints a geometry's vertices from the logo's blue (low end of `axis`) to cyan (high end). */
function paintGradient(geometry, axis = 'x') {
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox;
  const pos = geometry.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const c = new THREE.Color();
  const get = { x: 'getX', y: 'getY', z: 'getZ' }[axis];
  for (let i = 0; i < pos.count; i++) {
    const t = (pos[get](i) - min[axis]) / (max[axis] - min[axis] || 1);
    c.copy(BLUE).lerp(CYAN, t);
    colors.set([c.r, c.g, c.b], i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/* ---------- the reader ---------- */

/** A rectangle with its corners cut at 45°, like the reader's edges. */
function chamferRect(w, h, c) {
  const s = new THREE.Shape();
  const x = w / 2;
  const y = h / 2;
  s.moveTo(-x + c, -y);
  s.lineTo(x - c, -y);
  s.lineTo(x, -y + c);
  s.lineTo(x, y - c);
  s.lineTo(x - c, y);
  s.lineTo(-x + c, y);
  s.lineTo(-x, y - c);
  s.lineTo(-x, -y + c);
  s.closePath();
  return s;
}

/** The top label in the logo's colors: deep blue, a white curve, cyan with five indicator dots. No text. */
const LABEL_DOTS = { u: 0.84, v: [0.3, 0.4, 0.5, 0.6, 0.7] };
function labelTexture() {
  const W = 512;
  const H = 704;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  const blue = g.createLinearGradient(0, 0, W, H);
  blue.addColorStop(0, '#1A2A9E');
  blue.addColorStop(1, '#3B5BFF');
  g.fillStyle = blue;
  g.fillRect(0, 0, W, H);
  // The cyan side, bounded by a sweeping curve.
  const curve = () => {
    g.moveTo(W * 0.42, 0);
    g.bezierCurveTo(W * 0.62, H * 0.3, W * 0.72, H * 0.62, W * 0.7, H);
  };
  const cyan = g.createLinearGradient(W * 0.5, 0, W, H);
  cyan.addColorStop(0, '#22D3EE');
  cyan.addColorStop(1, '#0EA5C9');
  g.beginPath();
  curve();
  g.lineTo(W, H);
  g.lineTo(W, 0);
  g.closePath();
  g.fillStyle = cyan;
  g.fill();
  g.beginPath();
  curve();
  g.lineWidth = 16;
  g.strokeStyle = '#F4F7FB';
  g.stroke();
  g.fillStyle = '#05070B';
  for (const v of LABEL_DOTS.v) {
    g.beginPath();
    g.arc(W * LABEL_DOTS.u, H * v, 15, 0, Math.PI * 2);
    g.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/*
 * The reader, modeled on the real one (1 unit = 10 mm): a black body about
 * 46 × 24 × 70 mm with chamfered edges and vent slots on the sides, and the
 * OBD-II plug (about 40 mm wide) out the front (+z).
 */
function buildReader() {
  const reader = new THREE.Group();

  const shell = new THREE.MeshPhysicalMaterial({
    color: 0x0d1118,
    roughness: 0.5,
    metalness: 0.05,
    clearcoat: 0.6,
    clearcoatRoughness: 0.45,
  });
  const plastic = new THREE.MeshStandardMaterial({ color: 0x10151d, roughness: 0.6, metalness: 0.05 });
  const socket = new THREE.MeshStandardMaterial({ color: 0x040609, roughness: 0.95 });
  const slot = new THREE.MeshBasicMaterial({ color: 0x020305 });
  const metal = new THREE.MeshStandardMaterial({ color: 0xc9cdd4, roughness: 0.25, metalness: 1 });
  const glow = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });

  // Body: an octagonal section, extruded along z with flat chamfers at both ends.
  const bodyGeo = new THREE.ExtrudeGeometry(chamferRect(4.0, 1.8, 0.3), {
    depth: 6.4,
    bevelEnabled: true,
    bevelThickness: 0.3,
    bevelSize: 0.3,
    bevelSegments: 1,
  });
  bodyGeo.translate(0, 0, -3.2); // z -3.5 → 3.5; 4.6 wide, 2.4 tall
  reader.add(new THREE.Mesh(bodyGeo, shell));

  // The seam between the case halves, lit blue (back) to cyan (front) like the logo's arc.
  const seam = chamferRect(4.68, 7.08, 0.34);
  seam.holes.push(chamferRect(4.56, 6.96, 0.3));
  const seamGeo = new THREE.ExtrudeGeometry(seam, { depth: 0.05, bevelEnabled: false });
  seamGeo.rotateX(Math.PI / 2); // the outline now lies flat, its length along z
  seamGeo.center();
  paintGradient(seamGeo, 'z');
  reader.add(new THREE.Mesh(seamGeo, glow));

  // Vent slots along both sides.
  const slotGeo = new THREE.BoxGeometry(0.04, 1.05, 0.11);
  for (const side of [-1, 1]) {
    for (let i = 0; i < 9; i++) {
      const s = new THREE.Mesh(slotGeo, slot);
      s.position.set(side * 2.305, 0, -2.4 + i * 0.52);
      reader.add(s);
    }
  }

  // Top label, glossy. Its texture's top edge faces the plug.
  const labelW = 3.2;
  const labelL = 4.4;
  const labelZ = -0.45;
  const labelMap = labelTexture();
  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(labelW, labelL),
    new THREE.MeshPhysicalMaterial({
      map: labelMap,
      emissive: 0xffffff,
      emissiveMap: labelMap,
      emissiveIntensity: 0.28,
      roughness: 0.25,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    }),
  );
  label.rotation.x = -Math.PI / 2; // plane's +y (texture top) → -z; turn it so the top faces +z
  label.rotation.z = Math.PI;
  label.position.set(0, 1.206, labelZ);
  reader.add(label);

  // The first indicator dot lights up. With the label turned, texture u → -x and v (from the top) → -z from the plug end.
  const dot = (u, v) => new THREE.Vector3(-(u - 0.5) * labelW, 1.212, labelZ + (0.5 - v) * labelL);
  const led = new THREE.Mesh(
    new THREE.CircleGeometry(0.075, 24),
    new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }),
  );
  led.rotation.x = -Math.PI / 2;
  led.position.copy(dot(LABEL_DOTS.u, LABEL_DOTS.v[0]));
  reader.add(led);
  const halo = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: radialTexture([
        [0, 'rgba(255,255,255,1)'],
        [0.25, 'rgba(34,211,238,0.85)'],
        [1, 'rgba(34,211,238,0)'],
      ]),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    }),
  );
  halo.scale.setScalar(0.7);
  halo.position.copy(led.position);
  halo.position.y += 0.02;
  reader.add(halo);

  // The OBD-II plug: a trapezoid shroud, wide edge up, with a key tab under it.
  const plugY = -0.05;
  const shroud = trapezoid(4.0, 3.3, 1.6, 0.2);
  shroud.holes.push(trapezoid(3.64, 2.96, 1.26, 0.14, new THREE.Path()));
  const shroudMesh = new THREE.Mesh(
    new THREE.ExtrudeGeometry(shroud, {
      depth: 1.45,
      bevelEnabled: true,
      bevelThickness: 0.03,
      bevelSize: 0.03,
      bevelSegments: 2,
      curveSegments: 8,
    }),
    plastic,
  );
  shroudMesh.position.set(0, plugY, 3.45);
  reader.add(shroudMesh);

  const back = new THREE.Mesh(
    new THREE.ExtrudeGeometry(trapezoid(3.66, 2.98, 1.28, 0.14), { depth: 0.1, bevelEnabled: false }),
    socket,
  );
  back.position.set(0, plugY, 3.5);
  reader.add(back);

  const tab = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.22, 1.35), plastic);
  tab.position.set(0, plugY - 0.89, 4.2);
  reader.add(tab);

  // A ridge between the two rows of pins, and 16 pins.
  const divider = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.1, 1.15), socket);
  divider.position.set(0, plugY, 4.15);
  reader.add(divider);
  const pins = new THREE.InstancedMesh(new THREE.BoxGeometry(0.09, 0.09, 0.95), metal, 16);
  const m = new THREE.Matrix4();
  for (let i = 0; i < 16; i++) {
    const row = i < 8 ? 1 : -1;
    m.makeTranslation(((i % 8) - 3.5) * 0.4, plugY + row * 0.33, 4.1);
    pins.setMatrixAt(i, m);
  }
  reader.add(pins);

  // Turn around the middle of the whole reader, plug included.
  reader.position.z = -0.75;
  return { reader, halo, led };
}

/* ---------- the stage around it ---------- */

function buildStage(scene) {
  const stage = new THREE.Group();

  const floorGlow = new THREE.Mesh(
    new THREE.PlaneGeometry(10, 10),
    new THREE.MeshBasicMaterial({
      map: radialTexture([
        [0, 'rgba(34,211,238,0.55)'],
        [0.35, 'rgba(59,91,255,0.18)'],
        [1, 'rgba(59,91,255,0)'],
      ]),
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    }),
  );
  floorGlow.rotation.x = -Math.PI / 2;
  floorGlow.position.y = -1.9;
  stage.add(floorGlow);

  // Rings that pulse outward from under the reader, like a scan.
  const rings = [0, 0.5].map((offset) => {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1, 1.035, 96),
      new THREE.MeshBasicMaterial({
        color: CYAN,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -1.88;
    ring.userData.offset = offset;
    stage.add(ring);
    return ring;
  });

  // Drifting specks of light.
  const count = 140;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 2.5 + Math.random() * 4.5;
    positions.set([Math.cos(a) * r, -1.9 + Math.random() * 6, Math.sin(a) * r - 1], i * 3);
  }
  const specksGeo = new THREE.BufferGeometry();
  specksGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const specks = new THREE.Points(
    specksGeo,
    new THREE.PointsMaterial({
      color: CYAN,
      size: 0.05,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  stage.add(specks);

  scene.add(stage);
  return { rings, specks };
}

/** Reflections: a dark room with a white softbox above and the logo's colors on either side. */
function environment(renderer) {
  const room = new THREE.Scene();
  room.add(
    new THREE.Mesh(
      new THREE.BoxGeometry(20, 20, 20),
      new THREE.MeshBasicMaterial({ color: 0x05070b, side: THREE.BackSide }),
    ),
  );
  const light = (color, strength, position, size) => {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(size[0], size[1]),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(strength), side: THREE.DoubleSide }),
    );
    mesh.position.set(...position);
    mesh.lookAt(0, 0, 0);
    room.add(mesh);
  };
  light('#ffffff', 2.5, [0, 8, 2], [9, 4]);
  light('#22D3EE', 1.2, [-8, 1, -2], [2, 9]);
  light('#3B5BFF', 1.2, [8, 1, -2], [2, 9]);
  light('#ffffff', 0.8, [0, 2, 9], [7, 3]);
  const pmrem = new THREE.PMREMGenerator(renderer);
  const texture = pmrem.fromScene(room, 0.04).texture;
  pmrem.dispose();
  return texture;
}

/* ---------- mounting ---------- */

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const damp = (current, target, rate, dt) => current + (target - current) * (1 - Math.exp(-rate * dt));

function mount(el) {
  const canvas = el.querySelector('canvas');
  const mode = el.dataset.mode === 'scroll' ? 'scroll' : 'spin';
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch {
    return; // No WebGL: the poster stays.
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  scene.environment = environment(renderer);

  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(3, 6, 6);
  scene.add(key);
  const rimCyan = new THREE.PointLight(CYAN, 16, 20, 2);
  rimCyan.position.set(-5, 2, -4);
  scene.add(rimCyan);
  const rimBlue = new THREE.PointLight(BLUE, 16, 20, 2);
  rimBlue.position.set(5, 1, -4);
  scene.add(rimBlue);
  scene.add(new THREE.AmbientLight(0x8aa0c0, 0.25));

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  const tilt = new THREE.Group(); // leans the reader toward the camera
  const spinner = new THREE.Group(); // turns around the vertical axis
  const { reader, halo } = buildReader();
  spinner.add(reader);
  tilt.add(spinner);
  scene.add(tilt);
  const { rings, specks } = buildStage(scene);

  // Fit the camera so the reader fills the stage at any aspect ratio.
  const fitRadius = Number(el.dataset.fit || 4.9);
  const resize = () => {
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    const vHalf = THREE.MathUtils.degToRad(camera.fov / 2);
    const hHalf = Math.atan(Math.tan(vHalf) * camera.aspect);
    const distance = fitRadius / Math.sin(Math.min(vHalf, hHalf));
    camera.position.set(0, distance * 0.28, distance);
    camera.lookAt(0, -0.2, 0);
    camera.updateProjectionMatrix();
  };
  new ResizeObserver(resize).observe(el);
  resize();

  // Spin state: angle, speed, and drag.
  const autoSpeed = reduceMotion.matches ? 0 : 0.55; // radians per second
  let angle = mode === 'scroll' ? 0 : -0.7;
  let speed = autoSpeed;
  let dragging = false;
  let lastX = 0;
  let lastMoveTime = 0;
  let leanX = 0;
  let leanY = 0;

  if (mode === 'spin') {
    canvas.addEventListener('pointerdown', (e) => {
      dragging = true;
      lastX = e.clientX;
      lastMoveTime = performance.now();
      canvas.setPointerCapture(e.pointerId);
      el.classList.add('is-dragging');
    });
    canvas.addEventListener('pointermove', (e) => {
      if (dragging) {
        const now = performance.now();
        const dx = e.clientX - lastX;
        const turn = dx * 0.011;
        angle += turn;
        speed = clamp(turn / Math.max((now - lastMoveTime) / 1000, 0.008), -14, 14);
        lastX = e.clientX;
        lastMoveTime = now;
      } else if (e.pointerType === 'mouse') {
        const r = el.getBoundingClientRect();
        leanX = ((e.clientY - r.top) / r.height - 0.5) * 0.35;
        leanY = ((e.clientX - r.left) / r.width - 0.5) * 0.35;
      }
    });
    const release = () => {
      dragging = false;
      el.classList.remove('is-dragging');
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);
    canvas.addEventListener('pointerleave', () => {
      leanX = 0;
      leanY = 0;
    });
  }

  const story = el.closest('[data-ga-story]');
  const scrollProgress = () => {
    if (!story) return 0;
    const r = story.getBoundingClientRect();
    return clamp(-r.top / Math.max(r.height - window.innerHeight, 1), 0, 1);
  };

  let visible = false;
  let frame = 0;
  let last = performance.now();
  let time = 0;

  const render = (now) => {
    frame = 0;
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    time += dt;

    if (mode === 'spin') {
      if (!dragging) {
        speed = damp(speed, autoSpeed, 1.4, dt); // a flick spins fast, then eases back
        angle += speed * dt;
      }
      tilt.rotation.x = damp(tilt.rotation.x, 0.16 + leanX, 4, dt);
      tilt.rotation.z = damp(tilt.rotation.z, -leanY * 0.4, 4, dt);
    } else {
      // A full turn and a lean as the story scrolls by.
      const p = scrollProgress();
      angle = damp(angle, -0.6 + p * Math.PI * 2, 6, dt);
      tilt.rotation.x = damp(tilt.rotation.x, 0.12 + Math.sin(p * Math.PI) * 0.45, 6, dt);
    }
    spinner.rotation.y = angle;
    tilt.position.y = reduceMotion.matches ? 0 : Math.sin(time * 1.3) * 0.12;

    const pulse = 0.65 + 0.35 * Math.sin(time * 3.2);
    halo.material.opacity = pulse;
    halo.scale.setScalar(0.75 + pulse * 0.35);
    for (const ring of rings) {
      const t = (time * 0.45 + ring.userData.offset) % 1;
      ring.scale.setScalar(1.4 + t * 3.2);
      ring.material.opacity = (1 - t) * 0.5;
    }
    specks.rotation.y = time * 0.04;

    renderer.render(scene, camera);
    if (!el.classList.contains('is-ready')) el.classList.add('is-ready');
    if (visible && !document.hidden) frame = requestAnimationFrame(render);
  };

  const start = () => {
    if (!frame && visible && !document.hidden) {
      last = performance.now();
      frame = requestAnimationFrame(render);
    }
  };
  new IntersectionObserver(
    ([entry]) => {
      visible = entry.isIntersecting;
      start();
    },
    { rootMargin: '100px' },
  ).observe(el);
  document.addEventListener('visibilitychange', start);
}

document.querySelectorAll('[data-ga-reader]').forEach((el) => {
  try {
    mount(el);
  } catch (error) {
    console.warn('GasApp reader 3D:', error);
  }
});
